import { useEffect, useState } from "react";

// "Add to Home Screen" nudge for iOS Safari users.
// - Only renders on iOS Safari (not Chrome-on-iOS, not desktop, not Android)
// - Auto-hides when the app is already running as an installed PWA
// - Auto-hides after the user dismisses it (localStorage flag, no re-nag)
// - Animated share-icon arrow points down toward Safari's actual share button
export default function A2HSPrompt() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Query-param override: append ?a2hs=1 to the URL to force-show for testing.
    const forceShow = new URLSearchParams(window.location.search).get("a2hs") === "1";

    // 1) Skip if user already dismissed (unless forced)
    if (!forceShow && localStorage.getItem("pa_a2hs_dismissed") === "1") {
      console.info("[A2HS] hidden: previously dismissed");
      return;
    }

    // 2) Skip if already running as a standalone PWA (added to Home Screen)
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (!forceShow && isStandalone) {
      console.info("[A2HS] hidden: already installed as standalone PWA");
      return;
    }

    // 3) Show on any iOS device that isn't a known Chrome/Firefox/Edge/Opera
    //    variant. We do NOT strictly require "Safari" in the UA because some
    //    in-app browsers and WebViews on iOS omit that token — but the "Add
    //    to Home Screen" flow works from any WebKit-based iOS browser.
    const ua = window.navigator.userAgent;
    const isIOS =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1); // iPad-as-Mac
    const isForeignBrowser = /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
    if (!forceShow && (!isIOS || isForeignBrowser)) {
      console.info("[A2HS] hidden: not iOS Safari-family", { ua, isIOS, isForeignBrowser });
      return;
    }

    // Show after a short delay so it doesn't fight for attention with the login
    const t = setTimeout(() => setVisible(true), 900);
    return () => clearTimeout(t);
  }, []);

  function dismiss() {
    localStorage.setItem("pa_a2hs_dismissed", "1");
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div className="pa-a2hs-root" data-testid="a2hs-prompt">
      <style>{styles}</style>
      <div className="pa-a2hs-card">
        <div className="pa-a2hs-left">
          <div className="pa-a2hs-icon" aria-hidden="true">
            <ShareIcon />
          </div>
        </div>
        <div className="pa-a2hs-body">
          <div className="pa-a2hs-title">
            Install <em>Pharmacy Analyzer</em>
          </div>
          <div className="pa-a2hs-copy">
            <span className="pa-a2hs-step">1.</span> Tap <strong>•••</strong> or <ShareIconInline /> in Safari&apos;s toolbar
            <br />
            <span className="pa-a2hs-step">2.</span> Choose <strong>&ldquo;Add to Home Screen&rdquo;</strong>
            <br />
            <span className="pa-a2hs-step">3.</span> Tap <strong>Add</strong> — done!
          </div>
        </div>
        <button
          type="button"
          className="pa-a2hs-close"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          data-testid="a2hs-dismiss-btn"
        >
          ×
        </button>
      </div>
      <div className="pa-a2hs-arrow" aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="5" x2="12" y2="19" />
          <polyline points="19 12 12 19 5 12" />
        </svg>
      </div>
    </div>
  );
}

function ShareIcon() {
  // iOS-style share glyph (square with up-arrow)
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 6l4-4 4 4" />
      <line x1="12" y1="2" x2="12" y2="15" />
      <path d="M20 15v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-5" />
    </svg>
  );
}

function ShareIconInline() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "inline-block", verticalAlign: "-2px", margin: "0 2px" }}
    >
      <path d="M8 6l4-4 4 4" />
      <line x1="12" y1="2" x2="12" y2="15" />
      <path d="M20 15v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-5" />
    </svg>
  );
}

const styles = `
.pa-a2hs-root {
  position: fixed;
  left: 12px;
  right: 12px;
  bottom: calc(12px + env(safe-area-inset-bottom));
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  pointer-events: none;
  animation: pa-a2hs-in 320ms cubic-bezier(0.2, 0.9, 0.3, 1) both;
}
@keyframes pa-a2hs-in {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}
.pa-a2hs-card {
  pointer-events: auto;
  width: 100%;
  max-width: 420px;
  background: #FFFFFF;
  border: 1px solid rgba(27,59,46,0.08);
  border-radius: 18px;
  box-shadow:
    0 10px 24px rgba(27,59,46,0.14),
    0 2px 6px rgba(27,59,46,0.05);
  padding: 12px 14px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  align-items: center;
  font-family: 'Geist', 'Inter', -apple-system, sans-serif;
  color: #1B3B2E;
}
.pa-a2hs-left { display: flex; }
.pa-a2hs-icon {
  width: 36px; height: 36px;
  border-radius: 10px;
  background: #F0EAD9;
  color: #2B5E4A;
  display: flex; align-items: center; justify-content: center;
}
.pa-a2hs-body { min-width: 0; }
.pa-a2hs-title {
  font-family: 'Fraunces', serif;
  font-size: 14.5px;
  line-height: 1.1;
  color: #1B3B2E;
  margin-bottom: 3px;
  font-weight: 500;
}
.pa-a2hs-title em { font-style: italic; color: #2B5E4A; font-weight: 500; }
.pa-a2hs-copy {
  font-size: 11.5px;
  line-height: 1.45;
  color: #5D4A28;
}
.pa-a2hs-copy strong { color: #1B3B2E; font-weight: 600; }
.pa-a2hs-step {
  display: inline-block;
  width: 14px;
  color: #2B5E4A;
  font-weight: 600;
}
.pa-a2hs-close {
  align-self: flex-start;
  width: 26px; height: 26px;
  border-radius: 50%;
  border: none;
  background: rgba(27,59,46,0.06);
  color: #5D4A28;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s, color 0.15s;
}
.pa-a2hs-close:hover { background: rgba(27,59,46,0.12); color: #1B3B2E; }
.pa-a2hs-close:active { opacity: 0.7; }
.pa-a2hs-arrow {
  color: rgba(27,59,46,0.55);
  animation: pa-a2hs-bob 1.6s ease-in-out infinite;
}
@keyframes pa-a2hs-bob {
  0%, 100% { transform: translateY(0); opacity: 0.9; }
  50%      { transform: translateY(6px); opacity: 0.55; }
}
`;
